/* eslint-disable react/prop-types */
import React, { useEffect, useMemo } from 'react';
import styled, { withTheme } from 'styled-components';
import Tree, {
  mutateTree,
  moveItemOnTree,
} from '@atlaskit/tree';
import {
  Icon, Button, Text, Spacer
} from 'components/atoms'
import PropTypes from 'prop-types'
import { updateTree } from 'redux/sidenavSlice'
import { useSelector, useDispatch } from 'react-redux'
import ContentLoader from 'react-content-loader';
import navToTree from './parser/navToTree'
import { removeItemFromTree, computeDestination } from './parser/treeToNav'
import SideNavEditorItem from './SideNavEditorItem'

const Loader = withTheme(({ theme }) => (
  <LoaderWrapper>
    <ContentLoader
      height={88}
      width={330}
      speed={1}
      primaryColor={theme.colors.bgAnimation}
      secondaryColor={theme.colors.bgLighterS}
      title="a carregar histórico..."
    >
      <rect width="330" height="24" />
      <rect y="40" width="330" height="24" />
    </ContentLoader>
  </LoaderWrapper>
));

const SideNavEditor = ({
  initialProps, isLoading, currentSlug, error,
}) => {
  const dispatch = useDispatch()
  const { tree } = useSelector(
    (state) => state.sidenav
  )

  // generate sidenav editor
  useEffect(() => {
    if (!error) {
      const newTree = navToTree(initialProps, currentSlug)
      dispatch(updateTree(newTree))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProps, currentSlug, isLoading])

  const hasItems = useMemo(() => {
    const hasTreeItems = tree && tree.items && Object.keys(tree.items).length > 1
    return hasTreeItems
  }, [tree])

  const getIcon = (
    item,
    onExpand,
    onCollapse,
  ) => {
    if (item.children && item.children.length > 0) {
      return item.isExpanded ? (
        <Button
          modifiers={['xs3', 'invisible']}
          onClick={() => onCollapse(item.id)}
        >
          <Icon
            icon="expand-less"
            height={15}
            onClick={() => onCollapse(item.id)}
          />
        </Button>
      ) : (
        <Button
          modifiers={['xs3', 'invisible']}
          onClick={() => onExpand(item.id)}
        >
          <Icon
            icon="expand-more"
            height={15}
            onClick={() => onExpand(item.id)}
          />
        </Button>
      );
    }
    return null;
  }

  const removeItem = (item) => {
    const nextTree = removeItemFromTree(item.id, tree)
    dispatch(updateTree(nextTree))
  }

  const handleChangeTitle = (item, newTitle) => {
    const nextTree = mutateTree(
      tree,
      item.id,
      { data: { ...item.data, text: newTitle, title: newTitle } }
    )
    dispatch(updateTree(nextTree))
  }

  const onExpand = (itemId) => {
    dispatch(updateTree(mutateTree(tree, itemId, { isExpanded: true })));
  };

  const onCollapse = (itemId) => {
    dispatch(updateTree(mutateTree(tree, itemId, { isExpanded: false })))
  };

  const onDragEnd = (
    source,
    destination,
  ) => {
    if (!destination) {
      return;
    }
    const destinationSecondDepth = computeDestination({ tree, destination, source })
    let newTree = moveItemOnTree(tree, source, destinationSecondDepth);
    if (destination.parentId && destination.parentId !== 'root') {
      newTree = mutateTree(newTree, destination.parentId, { isExpanded: true })
    }
    dispatch(updateTree(newTree))
  };

  const renderItem = ({
    item,
    onExpand,
    onCollapse,
    provided,
    snapshot,
    depth,
    ...rest
  }) => (
    <SideNavEditorItem
      item={item}
      title={item.data ? item.data.title : ''}
      provided={provided}
      snapshot={snapshot}
      depth={depth}
      icon={getIcon(item, onExpand, onCollapse)}
      isCurrent={item.isCurrent}
      removeItem={removeItem}
      onChangeTitle={handleChangeTitle}
      tree={tree}
      {...rest}
    />
  )

  if (error) {
    return (
      <Container>
        <Spacer top="xs" />
        {error === 404
          ? (
            <Text>
            Menu por criar, comece por adicionar uma página ao menu.
            </Text>
          )
          : (
            <Text>
        Erro a carregar menu da subweb,
        &quot;
              {error}
        &quot;.
            </Text>
          )}
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container>
        <Loader />
      </Container>
    )
  }

  // empty sidenav
  if (!hasItems && !isLoading) {
    return (
      <Container>
        <Text>Sem items de menu.</Text>
      </Container>
    )
  }

  return (
    <>
      <Container>
        <Tree
          tree={tree}
          renderItem={renderItem}
          onExpand={onExpand}
          onCollapse={onCollapse}
          onDragEnd={onDragEnd}
          offsetPerLevel={24}
          isDragEnabled
          isNestingEnabled
        />
      </Container>
    </>
  );
}

SideNavEditor.propTypes = {
  initialProps: PropTypes.shape({
    tabs: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  currentSlug: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

SideNavEditor.defaultProps = {
  initialProps: {},
  currentSlug: null,
  isLoading: false,
  error: null,
}

const Container = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  justify-content: flex-start;
  max-width: ${props => props.theme.components.editor.sideNavEditorWidth};
  [data-react-beautiful-dnd-droppable="0"] {
    padding-bottom: 3rem;
  }
`;

const LoaderWrapper = styled.div`
  width: 330px;
  height: 88px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

export default SideNavEditor
